/*
 * Settings — Prepare Up's settings (#/profile): a section list on the left,
 * the chosen section on the right, on the same surface as Home and Practice.
 *
 * Sections: Account · Profile · Learning Preferences · Practice & Interviews ·
 * Notifications · Appearance · Privacy & Data · Integrations · Subscription.
 *
 * Real data only: account details come from the signed-in user, profile
 * fields from the profile API (ProfileForm, unchanged), the career goal from
 * the goal every page reads, and usage from the engine. Preferences the backend
 * profile can't hold yet are saved in this browser (settingsPrefs) and each
 * section says which choices take effect today.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { sendPasswordResetEmail } from 'firebase/auth';
import '../../styles/profile.css';
import '../../styles/prepare-up-progress.css';
import '../../styles/prepare-up-settings.css';
import APIService from '../../services/APIService';
import { useAuth } from '../../context/AuthContext';
import { auth } from '../../lib/firebase';
import IvShell, { useHubTree } from './InterviewChrome';
import { loadGoal, saveGoal } from './HomeDashboard';
import { buildPaths } from './paths';
import { LEVELS, ROLES, loadHistory } from './interviewData';
import { loadPrefs, savePrefs } from './settingsPrefs';
import {
  IconBolt, IconBrain, IconCheck, IconClock, IconDownload, IconKey, IconPalette, IconPerson, IconChevDown, IconHeart, IconShare, IconShield, IconTrash,
} from './icons';


// ---- Profile form (required: display_name, birthday, linkedin) --------------
const FIELDS = [
  ['display_name', 'Display name *', 'text', 'How you want to appear'],
  ['gender', 'Gender', 'select', ''],
  ['location', 'Location', 'text', 'City, Country'],
  ['birthday', 'Birthday *', 'date', ''],
  ['website', 'Website', 'url', 'https://…'],
  ['github', 'GitHub', 'text', 'username'],
  ['linkedin', 'LinkedIn *', 'text', 'profile URL or handle'],
  ['x', 'X', 'text', '@handle'],
];
const GENDERS = ['', 'Male', 'Female', 'Non-binary', 'Prefer not to say'];
export const REQUIRED_KEYS = ['display_name', 'birthday', 'linkedin'];
export const profileIncomplete = (p) => REQUIRED_KEYS.some((k) => !String(p?.[k] || '').trim());


// Simple square cropper: drag to pan, slider to zoom, Apply → 256px JPEG.
const AvatarCropper = ({ src, onApply, onCancel }) => {
  const V = 280;                                 // viewport px
  const [img, setImg] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const dragRef = React.useRef(null);

  useEffect(() => {
    const i = new Image();
    i.onload = () => { setImg(i); setZoom(1); setOff({ x: 0, y: 0 }); };
    i.src = src;
  }, [src]);

  if (!img) return null;
  const s0 = V / Math.min(img.width, img.height); // cover scale at zoom 1
  const scale = s0 * zoom;
  const w = img.width * scale;
  const h = img.height * scale;
  const clamp = (o) => ({
    x: Math.min(0, Math.max(V - w, o.x)),
    y: Math.min(0, Math.max(V - h, o.y)),
  });
  const pos = clamp({ x: off.x + (V - w) / 2, y: off.y + (V - h) / 2 });

  const onPointerDown = (e) => {
    dragRef.current = { x: e.clientX, y: e.clientY, ox: off.x, oy: off.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setOff({ x: dragRef.current.ox + (e.clientX - dragRef.current.x),
             y: dragRef.current.oy + (e.clientY - dragRef.current.y) });
  };
  const onPointerUp = () => { dragRef.current = null; };

  const apply = () => {
    const OUT = 256;
    const k = OUT / V;
    const c = document.createElement('canvas');
    c.width = OUT; c.height = OUT;
    const ctx = c.getContext('2d');
    ctx.drawImage(img, pos.x * k, pos.y * k, w * k, h * k);
    onApply(c.toDataURL('image/jpeg', 0.85));
  };

  return (
    <div className="pf-crop-veil" role="dialog" aria-label="Crop your photo">
      <div className="pf-crop">
        <div className="pf-crop-h">Crop your photo</div>
        <div className="pf-crop-view" style={{ width: V, height: V }}
          onPointerDown={onPointerDown} onPointerMove={onPointerMove}
          onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <img src={src} draggable={false}
            style={{ position: 'absolute', left: pos.x, top: pos.y, width: w, height: h, maxWidth: 'none' }} />
        </div>
        <label className="pf-crop-zoom">Zoom
          <input type="range" min="1" max="3" step="0.01" value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))} />
        </label>
        <div className="pf-crop-foot">
          <button className="pf-ghost" type="button" onClick={onCancel}>Cancel</button>
          <button className="pf-primary" type="button" onClick={apply}>Apply</button>
        </div>
      </div>
    </div>
  );
};

export const ProfileForm = ({ profile, onSaved, compact = false }) => {
  const [p, setP] = useState(profile || {});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [error, setError] = useState('');
  useEffect(() => { if (profile) setP(profile); }, [profile]);

  const set = (k, v) => setP((prev) => ({ ...prev, [k]: v }));
  const [cropSrc, setCropSrc] = useState(null);

  // Apply + persist the avatar IMMEDIATELY so it shows in the sidebar and on
  // Home without waiting for "Save changes" (unless the profile is still
  // incomplete — then it rides along with the required-fields save).
  const applyAvatar = async (dataUrl) => {
    let avatar = dataUrl;
    let avatarPath = '';
    if (!dataUrl) {
      try { await APIService.removeAvatar(); } catch { /* best-effort */ }
    } else {
      try {
        // The image goes to Cloud Storage; the profile doc keeps only the URL
        // (the server also swaps out the previous object).
        const up = await APIService.uploadAvatar(dataUrl);
        avatar = up.url || dataUrl;
        avatarPath = up.path || '';
      } catch { /* storage unavailable — keep the inline data URI */ }
    }
    const nextP = { ...p, avatar, avatar_path: avatarPath };
    setP(nextP);
    if (!profileIncomplete(nextP)) {
      APIService.saveProfile(nextP).then(() => onSaved?.(nextP)).catch(() => {});
    }
  };
  const lines = (k) => (Array.isArray(p?.[k]) ? p[k].join('\n') : '');
  const missing = REQUIRED_KEYS.filter((k) => !String(p?.[k] || '').trim());

  const save = async () => {
    if (missing.length) { setError(`Required: ${missing.map((k) => k.replace('_', ' ')).join(', ')}`); return; }
    setSaving(true); setError('');
    try {
      const clean = {
        ...p,
        work: (p.work || []).filter(Boolean),
        education: (p.education || []).filter(Boolean),
        skills: String(Array.isArray(p.skills) ? p.skills.join(', ') : p.skills || '')
          .split(',').map((s) => s.trim()).filter(Boolean),
      };
      await APIService.saveProfile(clean);
      setSavedAt(Date.now());
      onSaved?.(clean);
    } catch (e) {
      setError(e.message || 'Could not save your profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {error && <div className="pf-error">{error}</div>}
      <div className="pf-card">
        <div className="pf-card-h">Profile image</div>
        <div className="pf-avatar-row">
          {p.avatar
            ? <img className="pf-avatar" src={p.avatar} alt="Your avatar" />
            : <span className="pf-avatar ph">{(p.display_name || 'U').slice(0, 1).toUpperCase()}</span>}
          <label className="pf-ghost pf-upload">
            Upload image
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              e.target.value = '';
              if (!f) return;
              if (f.size > 2 * 1024 * 1024) { setError('Image must be under 2 MB.'); return; }
              setError('');
              setCropSrc(URL.createObjectURL(f));
            }} />
          </label>
          {p.avatar && <button className="pf-ghost" type="button" onClick={() => applyAvatar('')}>Remove</button>}
          <span className="pf-hint" style={{ margin: 0 }}>JPG/PNG up to 2 MB — you'll crop it next.</span>
        </div>
      </div>
      {cropSrc && <AvatarCropper src={cropSrc} onCancel={() => { URL.revokeObjectURL(cropSrc); setCropSrc(null); }}
        onApply={(dataUrl) => { URL.revokeObjectURL(cropSrc); setCropSrc(null); applyAvatar(dataUrl); }} />}

      <div className="pf-card">
        <div className="pf-card-h">Basic info</div>
        <div className="pf-grid">
          {FIELDS.map(([key, label, type, ph]) => (
            <label key={key} className="pf-field">
              <span>{label}</span>
              {type === 'select' ? (
                <select value={p[key] || ''} onChange={(e) => set(key, e.target.value)}>
                  {GENDERS.map((g) => <option key={g} value={g}>{g || '—'}</option>)}
                </select>
              ) : (
                <input type={type} value={p[key] || ''} placeholder={ph}
                  onChange={(e) => set(key, e.target.value)} />
              )}
            </label>
          ))}
        </div>
      </div>

      {!compact && (
        <>
          <div className="pf-card">
            <div className="pf-card-h">ReadMe</div>
            <p className="pf-hint">A short introduction in your own words — markdown welcome.</p>
            <textarea className="pf-textarea tall" value={p.readme || ''}
              placeholder="## About me…" onChange={(e) => set('readme', e.target.value)} />
          </div>
          <div className="pf-card">
            <div className="pf-card-h">Experience</div>
            <div className="pf-exp">
              <label className="pf-field">
                <span>Work <em>one role per line</em></span>
                <textarea className="pf-textarea" value={lines('work')}
                  placeholder={'ML Engineer @ Acme — 2022–now'}
                  onChange={(e) => set('work', e.target.value.split('\n'))} />
              </label>
              <label className="pf-field">
                <span>Education <em>one per line</em></span>
                <textarea className="pf-textarea" value={lines('education')}
                  placeholder={'B.Tech CS — 2015–2019'}
                  onChange={(e) => set('education', e.target.value.split('\n'))} />
              </label>
              <label className="pf-field">
                <span>Skills <em>comma separated</em></span>
                <input type="text"
                  value={Array.isArray(p.skills) ? p.skills.join(', ') : (p.skills || '')}
                  placeholder="Machine Learning, System Design, Python"
                  onChange={(e) => set('skills', e.target.value)} />
              </label>
            </div>
          </div>
        </>
      )}

      <div className="pf-formfoot">
        {savedAt > 0 && Date.now() - savedAt < 4000 && <span className="pf-saved">✓ Saved</span>}
        <button className="pf-primary" type="button" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : compact ? 'Save & continue' : 'Save changes'}
        </button>
      </div>
    </>
  );
};


// ---- Settings building blocks ------------------------------------------------
const SECTIONS = [
  ['account', 'Account', IconKey],
  ['profile', 'Profile', IconPerson],
  ['learning', 'Learning Preferences', IconBrain],
  ['practice', 'Practice & Interviews', IconBolt],
  ['notifications', 'Notifications', IconClock],
  ['appearance', 'Appearance', IconPalette],
  ['privacy', 'Privacy & Data', IconShield],
  ['integrations', 'Integrations', IconShare],
  ['subscription', 'Subscription', IconHeart],
];

const Group = ({ title, sub, action, children, className = '' }) => (
  <section className={`pu-panel st-group ${className}`}>
    {(title || action) && (
      <div className="st-group-head">
        <div>{title && <h2>{title}</h2>}{sub && <p>{sub}</p>}</div>
        {action}
      </div>
    )}
    {children}
  </section>
);
const Row = ({ label, hint, children, action }) => (
  <div className="st-row">
    <div className="st-row-k"><b>{label}</b>{hint && <small>{hint}</small>}</div>
    <div className="st-row-v">{children}</div>
    {action && <div className="st-row-a">{action}</div>}
  </div>
);
const Switch = ({ on, onChange, label }) => (
  <button type="button" role="switch" aria-checked={!!on} aria-label={label} className={`st-switch${on ? ' on' : ''}`} onClick={() => onChange(!on)}>
    <i aria-hidden="true" />
  </button>
);
const Seg = ({ value, options, onChange, label }) => (
  <div className="pg-seg st-seg" role="radiogroup" aria-label={label}>
    {options.map(([v, l]) => (
      <button key={String(v)} type="button" role="radio" aria-checked={value === v} className={value === v ? 'on' : ''} onClick={() => onChange(v)}>{l}</button>
    ))}
  </div>
);
const Select = ({ value, options, onChange, label }) => (
  <label className="kh-select st-select">
    <span className="pu-sr">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
      {options.map(([v, l, disabled]) => <option key={v} value={v} disabled={disabled}>{l}</option>)}
    </select>
    <IconChevDown size={14} />
  </label>
);
const Chips = ({ items, selected, onToggle, label }) => (
  <div className="st-chips" role="group" aria-label={label}>
    {items.map(([v, l]) => {
      const on = selected.includes(v);
      return <button key={v} type="button" aria-pressed={on} className={on ? 'on' : ''} onClick={() => onToggle(v)}>{on && <IconCheck size={12} />}{l}</button>;
    })}
  </div>
);
const Status = ({ ok, children }) => <span className={`st-status${ok ? ' ok' : ''}`}>{children}</span>;
const Note = ({ children }) => <p className="st-note">{children}</p>;
const DIFFS = [[null, 'Adaptive'], ['easy', 'Easy'], ['medium', 'Medium'], ['hard', 'Hard']];

// ---- Account (real auth data only) -------------------------------------------
const AccountSection = ({ user, profile, onSignOut }) => {
  const [resetSent, setResetSent] = useState(false);
  const [copied, setCopied] = useState(false);
  const providers = (user?.providerData || []).map((pd) => pd.providerId);
  const hasPassword = providers.includes('password');
  const puId = (user?.email || '').split('@')[0] || profile?.display_name || user?.uid || '—';
  const last = user?.metadata?.lastSignInTime ? new Date(user.metadata.lastSignInTime).toLocaleString() : null;

  const sendReset = async () => {
    if (!user?.email) return;
    try { await sendPasswordResetEmail(auth, user.email); setResetSent(true); } catch { /* ignore */ }
  };
  const copy = () => { try { navigator.clipboard.writeText(puId); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* no clipboard */ } };

  return (
    <>
      <Group title="Account" sub="Your sign-in details.">
        <Row label="Prepare Up ID" action={<button type="button" className="pu-link" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>}>{puId}</Row>
        <Row label="Email">
          {user?.email || '—'}
          {user?.email && <Status ok={user.emailVerified}>{user.emailVerified ? 'Verified' : 'Not verified'}</Status>}
        </Row>
        <Row label="Password"
          action={hasPassword && <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={sendReset} disabled={resetSent}>{resetSent ? 'Reset email sent' : 'Change password'}</button>}>
          {hasPassword ? '••••••••' : user ? 'Managed by your sign-in provider' : 'Not signed in'}
        </Row>
      </Group>
      <Group title="Sign-in methods" sub="How you sign in to Prepare Up.">
        {[['google.com', 'Google'], ['password', 'Email & password'], ['apple.com', 'Apple']].map(([id, label]) => (
          <Row key={id} label={label}>
            {providers.includes(id) ? <Status ok>Connected</Status> : <span className="st-muted">{id === 'apple.com' ? 'Not available yet' : 'Not connected'}</span>}
          </Row>
        ))}
      </Group>
      <Group title="Account security">
        <Row label="Email verification">{user?.emailVerified ? 'Your email is verified.' : user?.email ? 'Not verified yet.' : '—'}</Row>
        <Row label="Last sign-in">{last || '—'}</Row>
        <Row label="This device" action={onSignOut && user && <button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={onSignOut}>Sign out</button>}>
          {user ? 'Signed in' : 'Not signed in'}
        </Row>
      </Group>
    </>
  );
};

// ---- Profile ------------------------------------------------------------------
const ProfileSection = ({ profile, prefs, setPref, onSaved, editing, setEditing }) => {
  if (profile === null) return <div className="pu-empty">Loading your profile…</div>;
  const name = profile.display_name || 'Your name';
  const link = (v) => (v ? <span className="st-ellipsis">{v}</span> : <span className="st-muted">Not set</span>);
  if (editing) {
    return (
      <>
        <Group title="Edit profile" sub="Your professional identity — it helps Prepare Up personalise your practice."
          action={<button type="button" className="pu-btn pu-btn-sm pu-btn-primary" onClick={() => setEditing(false)}>Done</button>}>
          <div className="st-form">
            <label><span>Professional headline</span><input value={prefs.headline} placeholder="e.g. ML engineer moving into GenAI" onChange={(e) => setPref({ headline: e.target.value })} /></label>
            <label><span>Current role</span><input value={prefs.currentRole} placeholder="e.g. Data Analyst" onChange={(e) => setPref({ currentRole: e.target.value })} /></label>
            <label><span>Experience level</span>
              <select value={prefs.experienceLevel} onChange={(e) => setPref({ experienceLevel: e.target.value })}>
                <option value="">—</option>{LEVELS.map((l) => <option key={l}>{l}</option>)}
              </select>
            </label>
          </div>
          <Note>Headline, current role and experience level are saved on this device as you type.</Note>
        </Group>
        <div className="st-pf">
          <ProfileForm profile={profile} onSaved={onSaved} />
        </div>
      </>
    );
  }
  return (
    <Group title="Profile" sub="Your professional identity on Prepare Up."
      action={<button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={() => setEditing(true)}>Edit Profile</button>}>
      <div className="st-id">
        {profile.avatar ? <img className="st-avatar" src={profile.avatar} alt="" /> : <span className="st-avatar ph">{name.slice(0, 1).toUpperCase()}</span>}
        <div>
          <b>{name}</b>
          <span>{prefs.headline || <span className="st-muted">Add a headline</span>}</span>
        </div>
      </div>
      <Row label="Current role">{prefs.currentRole || <span className="st-muted">Not set</span>}</Row>
      <Row label="Experience level">{prefs.experienceLevel || <span className="st-muted">Not set</span>}</Row>
      <Row label="Location">{profile.location || <span className="st-muted">Not set</span>}</Row>
      <Row label="LinkedIn">{link(profile.linkedin)}</Row>
      <Row label="GitHub">{link(profile.github)}</Row>
      <Row label="Portfolio / Website">{link(profile.website)}</Row>
    </Group>
  );
};

// ---- Learning preferences ----------------------------------------------------------
const LearningSection = ({ hub, profile, onProfileSaved, prefs, setPref }) => {
  const paths = useMemo(() => (hub ? buildPaths(hub) : null), [hub]);
  const [goal, setGoal] = useState(loadGoal());
  const [skills, setSkills] = useState(() => (Array.isArray(profile?.skills) ? profile.skills.join(', ') : ''));
  const [skillsState, setSkillsState] = useState('');
  useEffect(() => { setSkills(Array.isArray(profile?.skills) ? profile.skills.join(', ') : ''); }, [profile]);
  const toggle = (key, v) => setPref({ [key]: prefs[key].includes(v) ? prefs[key].filter((x) => x !== v) : [...prefs[key], v] });
  const saveSkills = async () => {
    setSkillsState('saving');
    const next = { ...(profile || {}), skills: skills.split(',').map((x) => x.trim()).filter(Boolean) };
    try { await APIService.saveProfile(next); onProfileSaved?.(next); setSkillsState('saved'); } catch { setSkillsState('error'); }
  };
  const goalOptions = paths ? [...paths.roles, ...paths.areas] : [];
  return (
    <>
      <Group title="Goal & interests" sub="What you’re preparing for. Your goal shapes Home, Practice and Interview recommendations.">
        <Row label="Current career goal">
          <Select label="Current career goal" value={goal} onChange={(v) => { setGoal(v); saveGoal(v); }}
            options={[['', 'No goal set'], ...goalOptions.map((p) => [p.id, `${p.title}${p.kind === 'role' ? ' · career path' : ''}`])]} />
        </Row>
        <Row label="Career paths of interest">
          <Chips label="Career paths of interest" items={(paths?.roles || []).map((r) => [r.id, r.title])} selected={prefs.interests} onToggle={(v) => toggle('interests', v)} />
        </Row>
        <Row label="Topics of interest">
          <Chips label="Topics of interest" items={(hub || []).map((r) => [r.id, r.name])} selected={prefs.topics} onToggle={(v) => toggle('topics', v)} />
        </Row>
        <Row label="Skills you’re learning" hint="Saved to your profile"
          action={<button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={saveSkills} disabled={skillsState === 'saving'}>{skillsState === 'saving' ? 'Saving…' : skillsState === 'saved' ? 'Saved' : 'Save'}</button>}>
          <input className="st-input" value={skills} placeholder="Python, RAG, System design" onChange={(e) => { setSkills(e.target.value); setSkillsState(''); }} aria-label="Skills you’re learning" />
        </Row>
      </Group>
      <Group title="How you like to learn">
        <Row label="Learning style">
          <Seg label="Learning style" value={prefs.learningStyle} onChange={(v) => setPref({ learningStyle: v })}
            options={[['reading', 'Read first'], ['practice', 'Practice first'], ['mixed', 'Mixed']]} />
        </Row>
        <Row label="Practice frequency">
          <Select label="Practice frequency" value={prefs.frequency} onChange={(v) => setPref({ frequency: v })}
            options={[['daily', 'Every day'], ['few-week', 'A few times a week'], ['weekly', 'Once a week']]} />
        </Row>
        <Row label="Difficulty" hint="Default for practice and interviews">
          <Seg label="Difficulty" value={prefs.difficulty} onChange={(v) => setPref({ difficulty: v })} options={DIFFS} />
        </Row>
        <Row label="Interview preparation">
          <Seg label="Interview preparation" value={prefs.interviewPrep} onChange={(v) => setPref({ interviewPrep: v })}
            options={[['concepts', 'Concepts first'], ['balanced', 'Balanced'], ['interviews', 'Interview-heavy']]} />
        </Row>
        <Note>Your goal, skills and difficulty take effect now. Learning style, frequency, interests and interview preparation are saved on this device for upcoming personalisation.</Note>
      </Group>
    </>
  );
};

// ---- Practice & interviews -----------------------------------------------------------
const PracticeSection = ({ prefs, setPref }) => (
  <>
    <Group title="Practice" sub="Defaults when you start practising a topic.">
      <Row label="Default practice mode">
        <Select label="Default practice mode" value={prefs.practiceMode} onChange={(v) => setPref({ practiceMode: v })}
          options={[['mcq', 'MCQ'], ['theory', 'Theory'], ['flashcards', 'Flashcards'], ['case', 'Case Study']]} />
      </Row>
      <Row label="Preferred difficulty"><Seg label="Preferred difficulty" value={prefs.difficulty} onChange={(v) => setPref({ difficulty: v })} options={DIFFS} /></Row>
      <Row label="Questions per session"><Seg label="Questions per session" value={prefs.sessionSize} onChange={(v) => setPref({ sessionSize: v })} options={[[3, '3'], [5, '5'], [10, '10']]} /></Row>
      <Row label="Mixed practice" hint="Mix modes within a planned session"><Switch label="Mixed practice" on={prefs.mixedPractice} onChange={(v) => setPref({ mixedPractice: v })} /></Row>
      <Row label="Shuffle flashcards"><Switch label="Shuffle flashcards" on={prefs.flashcardShuffle} onChange={(v) => setPref({ flashcardShuffle: v })} /></Row>
      <Row label="Case study coaching hints"><Switch label="Case study coaching hints" on={prefs.caseStudyHints} onChange={(v) => setPref({ caseStudyHints: v })} /></Row>
      <Note>Mode, difficulty and questions per session pre-fill the “Practice this” dialog. Mixed practice, flashcard and case-study choices are saved for upcoming updates.</Note>
    </Group>
    <Group title="Interviews" sub="Defaults for Start AI Interview — you can still change everything before each interview.">
      <Row label="Preferred role">
        <Select label="Preferred role" value={prefs.interviewRoleId} onChange={(v) => setPref({ interviewRoleId: v })}
          options={[['', 'No preference'], ...ROLES.map((r) => [r.id, `${r.title}${r.rootIds.length ? '' : ' — coming soon'}`, !r.rootIds.length])]} />
      </Row>
      <Row label="Interview level">
        <Select label="Interview level" value={prefs.interviewLevel} onChange={(v) => setPref({ interviewLevel: v })} options={[['', 'No preference'], ...LEVELS.map((l) => [l, l])]} />
      </Row>
      <Row label="Interview type">
        <Seg label="Interview type" value={prefs.interviewType} onChange={(v) => setPref({ interviewType: v })} options={[['video', 'Video call'], ['voice', 'Voice call']]} />
      </Row>
      <Row label="Personalise AI interviews" hint="Use my practice history and weak areas">
        <Switch label="Personalise AI interviews" on={prefs.aiPersonalization} onChange={(v) => setPref({ aiPersonalization: v })} />
      </Row>
    </Group>
  </>
);

// ---- Notifications -----------------------------------------------------------------
const NotificationsSection = ({ prefs, setPref }) => (
  <Group title="Notifications" sub="Choose what Prepare Up should remind you about.">
    {[['notifyPractice', 'Practice reminders'], ['notifyLearning', 'Learning reminders'], ['notifyInterview', 'Interview reminders'],
      ['notifyProgress', 'Progress updates'], ['notifyWeekly', 'Weekly learning summary'], ['notifyProduct', 'Product updates']].map(([k, l]) => (
      <Row key={k} label={l}><Switch label={l} on={prefs[k]} onChange={(v) => setPref({ [k]: v })} /></Row>
    ))}
    <Note>Prepare Up doesn’t send reminders yet. Your choices are saved so they’re ready when it does.</Note>
  </Group>
);

// ---- Appearance ----------------------------------------------------------------------
const AppearanceSection = ({ themeMode, onSetTheme, prefs, setPref }) => (
  <Group title="Appearance">
    <Row label="Theme">
      <div className="st-themes" role="radiogroup" aria-label="Theme">
        {[['light', 'Light'], ['dark', 'Dark'], ['system', 'System']].map(([v, l]) => (
          <button key={v} type="button" role="radio" aria-checked={themeMode === v} className={`st-theme t-${v}${themeMode === v ? ' on' : ''}`} onClick={() => onSetTheme?.(v)}>
            <span className="st-theme-swatch" aria-hidden="true"><i /><i /></span>{l}
          </button>
        ))}
      </div>
    </Row>
    <Row label="Reduced motion" hint="Turns off animations and transitions">
      <Switch label="Reduced motion" on={prefs.reducedMotion} onChange={(v) => setPref({ reducedMotion: v })} />
    </Row>
  </Group>
);

// ---- Privacy & data -------------------------------------------------------------------
const PrivacySection = ({ user, profile, prefs, setPref }) => {
  const [busy, setBusy] = useState(false);
  const [cleared, setCleared] = useState(false);
  const download = async () => {
    setBusy(true);
    const safe = (pr) => pr.catch(() => null);
    const [prof, memory, activity] = await Promise.all([safe(APIService.getProfile()), safe(APIService.getMemoryTree()), safe(APIService.getEngineMemory())]);
    const data = {
      exported_at: new Date().toISOString(), account: { email: user?.email || null, uid: user?.uid || null },
      profile: prof || profile, learning_memory: memory, recent_activity: activity,
      interview_history: loadHistory(user?.uid), preferences: loadPrefs(), career_goal: loadGoal() || null,
    };
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a'); a.href = url; a.download = 'prepare-up-data.json'; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    setBusy(false);
  };
  const clearHistory = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Clear your interview history on this device? Feedback reports in it will be removed.')) return;
    try { localStorage.removeItem(`pu_interviews_v1:${user?.uid || 'local'}`); setCleared(true); } catch { /* private mode */ }
  };
  return (
    <>
      <Group title="Privacy & personalisation">
        <Row label="AI personalisation" hint="Use your practice history and weak areas to choose questions">
          <Switch label="AI personalisation" on={prefs.aiPersonalization} onChange={(v) => setPref({ aiPersonalization: v })} />
        </Row>
        <Row label="Help improve Prepare Up" hint="Use my activity to improve questions and feedback">
          <Switch label="Help improve Prepare Up" on={prefs.shareUsageForImprovement} onChange={(v) => setPref({ shareUsageForImprovement: v })} />
        </Row>
        <Row label="Interview history" hint="Stored on this device"
          action={<button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={clearHistory} disabled={cleared}>{cleared ? 'Cleared' : 'Clear history'}</button>}>
          {cleared ? 'Cleared' : `${loadHistory(user?.uid).length} saved interviews`}
        </Row>
        <Note>AI personalisation is the default for new AI interviews. “Help improve” is saved as your preference.</Note>
      </Group>
      <Group title="Your data">
        <Row label="Download my data" hint="Profile, learning memory, recent activity, interview history and preferences as JSON"
          action={<button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" onClick={download} disabled={busy}><IconDownload size={14} /> {busy ? 'Preparing…' : 'Download'}</button>}>
          <span className="st-muted">One file, generated now</span>
        </Row>
      </Group>
      <section className="pu-panel st-danger" aria-labelledby="st-del">
        <span className="st-danger-icon" aria-hidden="true"><IconTrash size={18} /></span>
        <div>
          <h2 id="st-del">Delete account</h2>
          <p>Permanently delete your account and all your data. Account deletion isn’t available in the app yet.</p>
        </div>
        <button type="button" className="pu-btn st-btn-danger" disabled title="Not available yet">Delete account</button>
      </section>
    </>
  );
};

// ---- Integrations ------------------------------------------------------------------------
const IntegrationsSection = ({ user, profile, onEditProfile }) => {
  const providers = (user?.providerData || []).map((pd) => pd.providerId);
  const linked = (v) => (v ? <Status ok>Connected</Status> : <span className="st-muted">Not connected</span>);
  return (
    <Group title="Integrations" sub="Accounts and links Prepare Up can use.">
      <Row label="Google" hint="Sign-in">{providers.includes('google.com') ? <Status ok>Connected</Status> : <span className="st-muted">Not connected</span>}</Row>
      {[['GitHub', 'github'], ['LinkedIn', 'linkedin'], ['Portfolio / Website', 'website']].map(([l, k]) => (
        <Row key={k} label={l} hint={profile?.[k] || undefined}
          action={<button type="button" className="pu-link" onClick={onEditProfile}>{profile?.[k] ? 'Edit' : 'Add'}</button>}>
          {linked(profile?.[k])}
        </Row>
      ))}
      <Row label="Calendar" hint="Schedule practice sessions"><span className="st-muted">Not available yet</span></Row>
    </Group>
  );
};

// ---- Subscription --------------------------------------------------------------------------
const SubscriptionSection = ({ user }) => {
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    APIService.getEngineMemory().then((d) => {
      const by = d?.by_surface || {};
      setUsage(Object.values(by).reduce((n, v) => n + (v.attempts || 0), 0));
    }).catch(() => setUsage(null));
  }, []);
  const interviews = loadHistory(user?.uid).length;
  return (
    <Group title="Subscription">
      <Row label="Current plan" action={<button type="button" className="pu-btn pu-btn-sm pu-btn-quiet" disabled title="Coming soon">Upgrade</button>}>
        <span className="st-plan">Free</span>
      </Row>
      <Row label="Plan details">All practice modes, AI and written interviews, Knowledge Hub and Progress — included during beta.</Row>
      <Row label="Usage">{usage == null ? '—' : `${new Intl.NumberFormat().format(usage)} questions practised`}{` · ${interviews} interview${interviews === 1 ? '' : 's'} on this device`}</Row>
      <Row label="Billing">Nothing to bill on the free plan.</Row>
    </Group>
  );
};

// ---- The Settings page ----------------------------------------------------------------------
export default function ProfileSettings({
  initialSection = 'account', profile: initialProfile, onProfileSaved,
  theme, themeMode, onSetTheme, onToggleTheme, onOpenHubNode, onOpenPath,
}) {
  const { user, signOut } = useAuth();
  const hub = useHubTree();
  const [section, setSection] = useState(initialSection);
  const [profile, setProfile] = useState(initialProfile || null);
  const [prefs, setPrefs] = useState(loadPrefs);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!initialProfile) APIService.getProfile().then(setProfile).catch(() => setProfile({}));
  }, [initialProfile]);
  useEffect(() => { if (initialProfile) setProfile(initialProfile); }, [initialProfile]);

  const setPref = (patch) => setPrefs((p) => { const next = { ...p, ...patch }; savePrefs(next); return next; });
  const onSaved = (p) => { setProfile(p); onProfileSaved?.(p); };
  const go = (id) => { setSection(id); setEditing(false); };
  const editProfile = () => { setSection('profile'); setEditing(true); };

  return (
    <IvShell hub={hub} className="pu-settings" profile={profile} onEditProfile={() => go('profile')}
      theme={theme} onToggleTheme={onToggleTheme} onOpenHubNode={onOpenHubNode} onOpenPath={onOpenPath}>
      <section className="st-head">
        <h1>Settings</h1>
        <p>Manage your account, preferences and learning experience.</p>
      </section>
      <div className="st-layout">
        <nav className="pu-panel st-nav" aria-label="Settings sections">
          {SECTIONS.map(([id, label, Icon]) => (
            <button key={id} type="button" className={section === id ? 'on' : ''} aria-current={section === id ? 'page' : undefined} onClick={() => go(id)}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </nav>
        <div className="st-main">
          {section === 'account' && <AccountSection user={user} profile={profile} onSignOut={signOut} />}
          {section === 'profile' && <ProfileSection profile={profile} prefs={prefs} setPref={setPref} onSaved={onSaved} editing={editing} setEditing={setEditing} />}
          {section === 'learning' && <LearningSection hub={hub} profile={profile} onProfileSaved={onSaved} prefs={prefs} setPref={setPref} />}
          {section === 'practice' && <PracticeSection prefs={prefs} setPref={setPref} />}
          {section === 'notifications' && <NotificationsSection prefs={prefs} setPref={setPref} />}
          {section === 'appearance' && <AppearanceSection themeMode={themeMode || theme} onSetTheme={onSetTheme} prefs={prefs} setPref={setPref} />}
          {section === 'privacy' && <PrivacySection user={user} profile={profile} prefs={prefs} setPref={setPref} />}
          {section === 'integrations' && <IntegrationsSection user={user} profile={profile} onEditProfile={editProfile} />}
          {section === 'subscription' && <SubscriptionSection user={user} />}
        </div>
      </div>
    </IvShell>
  );
}
